import { useState } from "react";
import { createReview } from "@/service/review";
import { Rating, Star } from "@smastrom/react-rating";
import { notify } from "@/components/Shares/Notification";
import { isEmpty } from "lodash";
import { IReview } from "@/interfaces/review";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import { isValidEmail } from "@/utils/check";
import { Button } from "@nextui-org/react";
import ImageMultipleUploader from "@/components/Shares/ImageMultipleUploader";
import { Image } from "@nextui-org/react";

interface ReviewProps {
  id: number;
  name: string;
  reviews: IReview[];
}

const Review: React.FC<ReviewProps> = (props) => {
  const { id, name, reviews } = props;
  const [rate, setRate] = useState(0);
  const [username, setUsername] = useState("");
  const [email, setEmail] = useState("");
  const [comment, setComment] = useState("");
  const [reviewList, setReviewList] = useState(reviews);
  const [images, setImages] = useState<string[]>([]);
  dayjs.extend(relativeTime);
  let now = dayjs();

  const handleRate = (selectedValue: number) => {
    setRate(selectedValue);
  };

  const handleSubtmit = async () => {
    if (rate === 0) {
      notify("Please rate this product before submit", "error");
      return;
    }
    if (!isValidEmail(email)) {
      notify("Email is invalid", "error");
      return;
    }
    const payload = {
      productVariantId: id,
      rate,
      name: username,
      email,
      comment,
      images,
    };
    const res = await createReview(payload);
    if (res.statusCode === 200) {
      setReviewList([...reviewList, res.data]);
      setUsername("");
      setEmail("");
      setComment("");
      setImages([]);
      notify("Thanks for your reivew", "success");
    }
  };

  return (
    <section>
      {isEmpty(reviewList) ? (
        <div>
          <p className="border-b pb-3">There are no reviews yet.</p>
          <h3 className="font-semibold text-base py-3">
            Be the first to review {`"${name}"`}
          </h3>
        </div>
      ) : (
        <>
          <h2 className="text-lg font-bold">Reviews</h2>
          <div className="flex flex-col py-6 gap-4">
            {reviewList.map((review) => {
              const { id, name, rate, comment, createdAt, images } = review;
              return (
                <div key={id} className="border-b pb-4">
                  <p className="font-bold">{name}</p>
                  <div className="flex gap-4">
                    <Rating
                      readOnly
                      value={rate}
                      style={{ maxWidth: 80 }}
                      itemStyles={{
                        itemShapes: Star,
                        activeFillColor: "#B79B6C",
                        inactiveFillColor: "#FFEACF",
                      }}
                    />
                    <p className="text-gray-400 text-sm">
                      {now.from(dayjs(createdAt))}
                    </p>
                  </div>
                  <p>{comment}</p>
                  {!isEmpty(images) && (
                    <div className="flex">
                      {images.map((image, index) => (
                        <div
                          key={index}
                          className="h-40 w-40 overflow-hidden flex items-center justify-center gap-2"
                        >
                          <Image src={image} alt="review image" radius="none" />
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </>
      )}
      <div className="flex flex-col gap-3 py-3">
        <div className="flex flex-col bg-gray-100 px-8 py-12">
          <p className="mb-6">
            Your email address will not be published. Required fields are marked
            *
          </p>
          <div className="text-lg mb-4 flex items-center gap-4">
            <p>Rate this product: *</p>
            <Rating
              onChange={handleRate}
              value={rate}
              style={{ maxWidth: 200 }}
              itemStyles={{
                itemShapes: Star,
                activeFillColor: "#B79B6C",
                inactiveFillColor: "#FFEACF",
              }}
            />
          </div>
          <div className="flex flex-col gap-2 mb-4">
            <label className="text-semibold">Leave a feedback</label>
            <textarea
              className=" px-4 py-2  resize-none outline-none border border-stroke rounded-md focus:border-icon focus:shadow-md"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              rows={5}
            />
          </div>
          <div className="flex flex-col gap-1 select-none mb-2">
            <label className="text-semibold">
              Attach photos to your review
            </label>
            <ImageMultipleUploader files={images} setFiles={setImages} />
          </div>
          <div className="flex flex-col gap-1 select-none mb-2">
            <label className="text-semibold">Name *</label>
            <input
              className="select-none px-4 py-3  resize-none outline-none border border-stroke rounded-md focus:border-icon focus:shadow-md"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
            />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-semibold">Email *</label>
            <input
              className="select-none px-4 py-3 resize-none outline-none border border-stroke rounded-md focus:border-icon focus:shadow-md"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>
          <Button
            className="w-48 select-none text-white px-5 py-6 rounded-md mt-5 shadow-card bg-icon transition-all duration-500"
            onClick={() => handleSubtmit()}
          >
            Submit review
          </Button>
        </div>
      </div>
    </section>
  );
};

export default Review;
